import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  ArrowLeftIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  EllipsisVerticalIcon,
  InboxIcon,
  LanguageIcon,
  PaperClipIcon,
} from '@heroicons/react/24/outline';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import {
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import EmptyState from '../../components/EmptyState/EmptyState';
import { ErrorAlert } from '../../components/ErrorAlert/ErrorAlert';
import FilterChip from '../../components/FilterChip/FilterChip';
import Loader from '../../components/Loader/Loader';
import Badge from '../../components/Styled/Badge';
import { StyledLink } from '../../components/Styled/StyledLink';
import {
  SupportMessage,
  SupportTicket,
  SupportTicketNote,
  SupportTicketTranslation,
} from '../../graphql/graphql';
import {
  ADD_SUPPORT_TICKET_NOTE,
  REPLY_SUPPORT_TICKET,
  SET_SUPPORT_TICKET_STATUS,
} from '../../graphql/mutations-gql';
import {
  SUPPORT_TICKET,
  TRANSLATE_SUPPORT_TICKET,
} from '../../graphql/queries-gql';
import { useDismissableSnackbar } from '../../hooks/useDismissableSnackbar';
import {
  categoryLabel,
  formatBytes,
  formatFullTime,
  isSnoozed,
  sourceLabel,
  statusMeta,
} from './supportMeta';

const POLL_INTERVAL_MS = 60_000;
const SNOOZE_DAYS = [1, 3, 7, 14];
// The list and the counts move whenever a ticket changes status
const REFETCH_AFTER_CHANGE = ['supportTickets', 'supportTicketCounts'];

const Header = styled.header`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  border-bottom: 1px solid ${(props) => props.theme.border};
`;

const HeaderTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const BackButton = styled.span`
  display: none;

  @media (max-width: 900px) {
    display: inline-flex;
  }
`;

const Subject = styled.h2`
  flex: 1;
  min-width: 0;
  margin: 3px 0 0;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.35;
  overflow-wrap: anywhere;
  color: ${(props) => props.theme.textColor};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 8px;
  font-size: 12.5px;
  color: ${(props) => props.theme.secondaryTextColor};
  overflow-wrap: anywhere;
`;

const TicketNumber = styled.span`
  font-family: ${(props) => props.theme.monoFontFamily};
  font-size: 12px;
  color: ${(props) => props.theme.mutedTextColor};
`;

const Translation = styled.span`
  display: block;
  font-size: 13px;
  font-weight: 400;
  font-style: italic;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.borderStrong};
    border-radius: 3px;
  }
`;

const MessageGroup = styled.div<{ $inbound: boolean; $dimmed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 78%;
  opacity: ${(props) => (props.$dimmed ? 0.6 : 1)};
  align-self: ${(props) => (props.$inbound ? 'flex-start' : 'flex-end')};
  align-items: ${(props) => (props.$inbound ? 'flex-start' : 'flex-end')};

  @media (max-width: 900px) {
    max-width: 92%;
  }
`;

// Outgoing bubbles sit on the brand fill, so their text is always white.
const Bubble = styled.div<{ $inbound: boolean }>`
  padding: 9px 13px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  background-color: ${(props) =>
    props.$inbound ? props.theme.surfaceRaised : props.theme.colors.primary};
  color: ${(props) => (props.$inbound ? props.theme.textColor : '#fff')};
  border: 1px solid
    ${(props) => (props.$inbound ? props.theme.border : 'transparent')};
  border-bottom-left-radius: ${(props) => (props.$inbound ? '4px' : '14px')};
  border-bottom-right-radius: ${(props) => (props.$inbound ? '14px' : '4px')};
`;

const BubbleTranslation = styled.div`
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid ${(props) => props.theme.border};
  font-style: italic;
  color: ${(props) => props.theme.secondaryTextColor};
`;

const MessageMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  padding: 0 4px;
  font-size: 11.5px;
  color: ${(props) => props.theme.mutedTextColor};
  font-variant-numeric: tabular-nums;
`;

const TextButton = styled.button`
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: ${(props) => props.theme.colors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Attachments = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const NoteRow = styled.div`
  align-self: center;
  max-width: 86%;
  padding: 7px 12px;
  border: 1px dashed ${(props) => props.theme.borderStrong};
  border-radius: 10px;
  font-size: 12.5px;
  line-height: 1.45;
  text-align: center;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${(props) => props.theme.secondaryTextColor};
  background: ${(props) => props.theme.surfaceHover};
`;

const NoteMeta = styled.span`
  display: block;
  margin-bottom: 2px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(props) => props.theme.mutedTextColor};
`;

const Composer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 16px 14px;
  border-top: 1px solid ${(props) => props.theme.border};
`;

const ComposerRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const ComposerHint = styled.span`
  flex: 1;
  min-width: 160px;
  font-size: 12.5px;
  color: ${(props) => props.theme.mutedTextColor};
  overflow-wrap: anywhere;
`;

const CenterFill = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;
  padding: 24px;
`;

type TimelineItem =
  | { kind: 'message'; at: number; key: string; message: SupportMessage }
  | { kind: 'note'; at: number; key: string; note: SupportTicketNote };

const SENT_BY_LABELS: Record<string, string> = {
  emil: 'Admin',
  auto: 'Auto reply',
};

const buildTimeline = (ticket: SupportTicket): TimelineItem[] => {
  const items: TimelineItem[] = [
    ...ticket.messages.map((message) => ({
      kind: 'message' as const,
      at: new Date(message.receivedAt).getTime(),
      key: message._id,
      message,
    })),
    ...ticket.notes.map((note, index) => ({
      kind: 'note' as const,
      at: new Date(note.at).getTime(),
      key: `note-${index}`,
      note,
    })),
  ];
  return items.sort((a, b) => a.at - b.at);
};

// Why a reply cannot be sent right now, or null when it can.
const replyBlocker = (ticket: SupportTicket): string | null => {
  if (ticket.source === 'system') {
    return 'System tickets come from our own backends and cannot be replied to.';
  }
  if (ticket.status === 'spam' || ticket.status === 'archived') {
    return 'Reopen the ticket to reply.';
  }
  return null;
};

// One ticket: its header, the conversation with internal notes in between,
// and the composer for a reply or a note.
const TicketThread: React.FC<{
  ticketId: string;
  onBack: () => void;
}> = ({ ticketId, onBack }) => {
  const { showSnackbar } = useDismissableSnackbar();
  const [mode, setMode] = useState<'reply' | 'note'>('reply');
  const [text, setText] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const timelineEndRef = useRef<HTMLDivElement>(null);

  const { data, loading, error } = useQuery<{
    supportTicket: SupportTicket | null;
  }>(SUPPORT_TICKET, {
    variables: { _id: ticketId },
    fetchPolicy: 'cache-and-network',
    pollInterval: POLL_INTERVAL_MS,
  });

  const [translate, { data: translationData, loading: translating }] =
    useLazyQuery<{ translateSupportTicket: SupportTicketTranslation }>(
      TRANSLATE_SUPPORT_TICKET,
      {
        variables: { _id: ticketId },
        fetchPolicy: 'network-only',
        onError: (err) => showSnackbar(err.message, 'error'),
      }
    );

  const mutationOptions = {
    refetchQueries: REFETCH_AFTER_CHANGE,
    onError: (err: Error) => showSnackbar(err.message, 'error'),
  };
  const [replyTicket, { loading: replying }] = useMutation(
    REPLY_SUPPORT_TICKET,
    mutationOptions
  );
  const [setTicketStatus, { loading: settingStatus }] = useMutation(
    SET_SUPPORT_TICKET_STATUS,
    mutationOptions
  );
  const [addNote, { loading: addingNote }] = useMutation(
    ADD_SUPPORT_TICKET_NOTE,
    mutationOptions
  );

  const ticket = data?.supportTicket ?? null;
  // The list query caches tickets without their thread, so a ticket can be
  // in the cache before its messages are.
  const threadLoaded = !!ticket && Array.isArray(ticket.messages);
  const timeline = useMemo(
    () => (ticket && threadLoaded ? buildTimeline(ticket) : []),
    [ticket, threadLoaded]
  );

  const translations = useMemo(() => {
    const byMessageId = new Map<string, string>();
    translationData?.translateSupportTicket.messages.forEach((m) =>
      byMessageId.set(m.messageId, m.text)
    );
    return byMessageId;
  }, [translationData]);

  useEffect(() => {
    timelineEndRef.current?.scrollIntoView({ block: 'end' });
  }, [ticketId, timeline.length]);

  if (!threadLoaded || !ticket) {
    return (
      <CenterFill>
        {error ? (
          <ErrorAlert error={error} />
        ) : loading ? (
          <Loader />
        ) : (
          <EmptyState
            Icon={InboxIcon}
            title="Ticket not found"
            description="It may have been removed. Pick another one from the list."
          />
        )}
      </CenterFill>
    );
  }

  const status = statusMeta(ticket);
  const closed = ['resolved', 'spam', 'archived'].includes(ticket.status);
  const blocker = replyBlocker(ticket);
  const busy = replying || settingStatus || addingNote;
  const trimmed = text.trim();
  const translatedSubject = translationData?.translateSupportTicket.subject;

  const changeStatus = async (
    nextStatus: string,
    message: string,
    snoozeDays?: number
  ) => {
    setMenuAnchor(null);
    const result = await setTicketStatus({
      variables: { _id: ticket._id, status: nextStatus, snoozeDays },
    });
    if (result.data) showSnackbar(message, 'success');
  };

  const sendReply = async (resolve: boolean) => {
    if (!trimmed || blocker || busy) return;
    const result = await replyTicket({
      variables: { _id: ticket._id, text: trimmed, resolve },
    });
    if (result.data) {
      setText('');
      showSnackbar(resolve ? 'Reply sent, ticket resolved' : 'Reply sent', 'success');
    }
  };

  const saveNote = async () => {
    if (!trimmed || busy) return;
    const result = await addNote({
      variables: { _id: ticket._id, text: trimmed },
    });
    if (result.data) {
      setText('');
      showSnackbar('Note added', 'success');
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      if (mode === 'reply') sendReply(false);
      else saveNote();
    }
  };

  return (
    <>
      <Header>
        <HeaderTop>
          <BackButton>
            <IconButton size="small" onClick={onBack} aria-label="Back to tickets">
              <ArrowLeftIcon width={18} />
            </IconButton>
          </BackButton>
          <Subject>
            {ticket.subject || '(no subject)'}
            {translatedSubject && translatedSubject !== ticket.subject && (
              <Translation>{translatedSubject}</Translation>
            )}
          </Subject>
          <HeaderActions>
            <Tooltip title="Translate to English">
              <span>
                <IconButton
                  size="small"
                  onClick={() => translate()}
                  disabled={translating}
                  aria-label="Translate to English"
                >
                  {translating ? <Loader size={16} /> : <LanguageIcon width={18} />}
                </IconButton>
              </span>
            </Tooltip>
            {closed || isSnoozed(ticket) ? (
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                startIcon={<ArrowUturnLeftIcon width={16} />}
                onClick={() => changeStatus('open', 'Ticket reopened')}
              >
                Reopen
              </Button>
            ) : (
              <Button
                size="small"
                variant="contained"
                disabled={busy}
                startIcon={<CheckCircleIcon width={16} />}
                onClick={() => changeStatus('resolved', 'Ticket resolved')}
              >
                Resolve
              </Button>
            )}
            <IconButton
              size="small"
              disabled={busy}
              onClick={(event) => setMenuAnchor(event.currentTarget)}
              aria-label="More actions"
            >
              <EllipsisVerticalIcon width={18} />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={!!menuAnchor}
              onClose={() => setMenuAnchor(null)}
            >
              {SNOOZE_DAYS.map((days) => (
                <MenuItem
                  key={days}
                  disabled={closed}
                  onClick={() =>
                    changeStatus(
                      'snoozed',
                      `Snoozed for ${days} ${days === 1 ? 'day' : 'days'}`,
                      days
                    )
                  }
                >
                  Snooze {days} {days === 1 ? 'day' : 'days'}
                </MenuItem>
              ))}
              <Divider />
              <MenuItem
                disabled={ticket.status === 'open' && !isSnoozed(ticket)}
                onClick={() => changeStatus('open', 'Ticket marked as open')}
              >
                Mark as open
              </MenuItem>
              <MenuItem
                disabled={ticket.status === 'resolved'}
                onClick={() => changeStatus('resolved', 'Ticket resolved')}
              >
                Resolve
              </MenuItem>
              <MenuItem
                disabled={ticket.status === 'archived'}
                onClick={() => changeStatus('archived', 'Ticket archived')}
              >
                Archive
              </MenuItem>
              <MenuItem
                disabled={ticket.status === 'spam'}
                onClick={() => changeStatus('spam', 'Marked as spam')}
              >
                Mark as spam
              </MenuItem>
            </Menu>
          </HeaderActions>
        </HeaderTop>

        <MetaRow>
          <TicketNumber>{ticket.displayId}</TicketNumber>
          <Badge $tone={status.tone}>{status.label}</Badge>
          {ticket.needsAdmin && <Badge $tone="red">Needs admin</Badge>}
          <Badge>{sourceLabel(ticket.source)}</Badge>
          {ticket.category && <Badge>{categoryLabel(ticket.category)}</Badge>}
          {ticket.language && <Badge>{ticket.language.toUpperCase()}</Badge>}
          {ticket.blockedSender && <Badge $tone="red">Blocked sender</Badge>}
          {ticket.spamScore > 0 && (
            <Tooltip title={ticket.spamReasons.join(', ') || 'Spam heuristics'}>
              <Badge $tone="orange">Spam score {ticket.spamScore}</Badge>
            </Tooltip>
          )}
          {ticket.autoReplyCount > 0 && (
            <Badge>
              {ticket.autoReplyCount} auto{' '}
              {ticket.autoReplyCount === 1 ? 'reply' : 'replies'}
            </Badge>
          )}
        </MetaRow>

        <MetaRow>
          <span>
            From {ticket.senderName ? `${ticket.senderName}, ` : ''}
            {ticket.senderEmail}
          </span>
          {ticket.userId && (
            <StyledLink to={`/user/${ticket.userId}`}>
              {ticket.user?.username ? `@${ticket.user.username}` : 'Linked user'}
            </StyledLink>
          )}
          {!ticket.userId && <span>No linked account</span>}
          {ticket.user?.country && <span>{ticket.user.country}</span>}
          {ticket.user?.premium && <Badge $tone="orange">Premium</Badge>}
          {ticket.user?.banned && <Badge $tone="red">Banned</Badge>}
          {ticket.user?.deleted && <Badge $tone="red">Deleted</Badge>}
        </MetaRow>

        {ticket.report && (
          <MetaRow>
            <span>
              Reported {ticket.report.type.toLowerCase()} for{' '}
              {ticket.report.problem.toLowerCase().replace(/_/g, ' ')}
            </span>
            {ticket.report.contentId && (
              <span>
                {categoryLabel(ticket.report.type.toLowerCase())} ID:{' '}
                {ticket.report.type.toUpperCase() === 'USER' ? (
                  <StyledLink to={`/user/${encodeURIComponent(ticket.report.contentId)}`}>
                    <TicketNumber>{ticket.report.contentId}</TicketNumber>
                  </StyledLink>
                ) : (
                  <TicketNumber>{ticket.report.contentId}</TicketNumber>
                )}
              </span>
            )}
            {ticket.report.reportedUserId && (
              <StyledLink to={`/user/${ticket.report.reportedUserId}`}>
                {ticket.report.reportedUsername
                  ? `@${ticket.report.reportedUsername}`
                  : 'Reported user'}
              </StyledLink>
            )}
          </MetaRow>
        )}

        {isSnoozed(ticket) && ticket.snoozedUntil && (
          <MetaRow>Snoozed until {formatFullTime(ticket.snoozedUntil)}</MetaRow>
        )}

        {closed && ticket.resolution && (
          <MetaRow>
            {status.label} by {ticket.resolution.by},{' '}
            {formatFullTime(ticket.resolution.at)}
            {ticket.resolution.note ? `: ${ticket.resolution.note}` : ''}
          </MetaRow>
        )}
      </Header>

      <Timeline>
        {timeline.map((item) => {
          if (item.kind === 'note') {
            return (
              <NoteRow key={item.key}>
                <NoteMeta>
                  Note by {item.note.by}, {formatFullTime(item.note.at)}
                </NoteMeta>
                {item.note.text}
              </NoteRow>
            );
          }

          const { message } = item;
          const inbound = message.direction === 'inbound';
          const showFull = !!expanded[message._id] && !!message.fullText;
          const translation = translations.get(message._id);
          const sender = inbound
            ? message.fromName || ticket.senderName || 'User'
            : message.via === 'backoffice'
            ? 'Admin chat'
            : SENT_BY_LABELS[message.sentBy ?? ''] ?? 'Support';

          return (
            <MessageGroup
              key={item.key}
              $inbound={inbound}
              $dimmed={message.historical}
            >
              <Bubble $inbound={inbound}>
                {showFull ? message.fullText : message.text || '(empty message)'}
                {translation && translation !== message.text && (
                  <BubbleTranslation>{translation}</BubbleTranslation>
                )}
              </Bubble>
              {message.attachments.length > 0 && (
                <Attachments>
                  {message.attachments.map((attachment, index) => (
                    <Tooltip
                      key={`${attachment.name}-${index}`}
                      title={`${attachment.mime}. Attachments are stored with the mail and cannot be opened here.`}
                    >
                      <Badge>
                        <PaperClipIcon />
                        {attachment.name} ({formatBytes(attachment.size)})
                      </Badge>
                    </Tooltip>
                  ))}
                </Attachments>
              )}
              <MessageMeta>
                <span>
                  {sender}, {formatFullTime(message.receivedAt)}
                </span>
                {message.historical && <span>Earlier context</span>}
                {message.autoSubmitted && inbound && <span>Automatic mail</span>}
                {message.bounced && <Badge $tone="red">Bounced</Badge>}
                {message.inAppStatus === 'sent' && <span>Also sent in app</span>}
                {message.inAppStatus === 'failed' && (
                  <Badge $tone="orange">In-app copy failed</Badge>
                )}
                {message.fullText && (
                  <TextButton
                    type="button"
                    onClick={() =>
                      setExpanded((current) => ({
                        ...current,
                        [message._id]: !current[message._id],
                      }))
                    }
                  >
                    {showFull ? 'Hide quoted text' : 'Show full mail'}
                  </TextButton>
                )}
              </MessageMeta>
            </MessageGroup>
          );
        })}
        <div ref={timelineEndRef} />
      </Timeline>

      <Composer>
        <ComposerRow>
          <FilterChip
            label="Reply"
            selected={mode === 'reply'}
            onChange={() => setMode('reply')}
          />
          <FilterChip
            label="Internal note"
            selected={mode === 'note'}
            onChange={() => setMode('note')}
          />
        </ComposerRow>
        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={10}
          size="small"
          variant="outlined"
          placeholder={
            mode === 'reply'
              ? 'Write a reply to the user...'
              : 'Write a note for the team. The user never sees it.'
          }
          value={text}
          disabled={mode === 'reply' && !!blocker}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <ComposerRow>
          <ComposerHint>
            {mode === 'note'
              ? 'Notes stay on the ticket and are never sent.'
              : blocker ??
                (ticket.channel === 'chat'
                  ? "Posted in the user's support chat in the app."
                  : `Sent by email to ${ticket.senderEmail}.`)}
          </ComposerHint>
          {mode === 'note' ? (
            <Button
              variant="contained"
              disabled={!trimmed || busy}
              onClick={saveNote}
            >
              Add note
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                disabled={!trimmed || busy || !!blocker}
                onClick={() => sendReply(false)}
              >
                Send
              </Button>
              <Button
                variant="contained"
                disabled={!trimmed || busy || !!blocker}
                endIcon={<PaperAirplaneIcon width={16} />}
                onClick={() => sendReply(true)}
              >
                Send and resolve
              </Button>
            </>
          )}
        </ComposerRow>
      </Composer>
    </>
  );
};
export default TicketThread;
